import React from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

import baseStyles from '@components/CarouselSection/CarouselItem.module.css';
import { Link } from '@components/Link';
import { RecipeDifficultyIcon } from '@components/RecipeDifficultyIcon';
import { SanityImage } from '@components/SanityImage';

import { CmsRecipeListCard, HeadingLevel } from '@interfaces/common';
import { minutesToISODuration } from '@utils/date';
import { breakpoints } from '@utils/device';
import { getHeadingTag } from '@utils/heading';
import { intlRecipeDifficulty, intlRecipeDuration } from '@utils/intl';

import styles from './RecipeListCard.module.css';

interface RecipeListCardProps extends CmsRecipeListCard {
	className?: string;
	headingLevel?: HeadingLevel;
}

export const RecipeListCard: React.VFC<RecipeListCardProps> = ({
	image,
	title,
	subtitle,
	subtitleColor,
	slug,
	totalTime,
	difficulty,
	className,
	headingLevel = 3,
}) => {
	const intl = useIntl();
	const Heading = getHeadingTag(headingLevel);
	const customProperties = {
		'--carousel-item-subtitle-color':
			subtitleColor || 'var(--color-core-black)',
	} as React.CSSProperties;

	return (
		<Link
			className={classNames(
				baseStyles.item,
				styles.item,
				className,
				'stack-block'
			)}
			href={`/recipes/${slug}`}
			style={customProperties}
		>
			<div className={baseStyles['image-wrapper']}>
				<SanityImage
					className={baseStyles.image}
					image={image}
					sizes={`${breakpoints.md} 40vw, ${breakpoints.lg} 25vw, 80vw`}
				/>

				<div className={styles.info}>
					<div className="stack-inline">
						<RecipeDifficultyIcon difficulty={difficulty} theme="light" />
						<span className="fluid-font-t6">
							{intlRecipeDifficulty(intl, difficulty)}
						</span>
					</div>

					<time dateTime={minutesToISODuration(totalTime)}>
						{intlRecipeDuration(intl, totalTime)}
					</time>
				</div>
			</div>

			<Heading className={classNames(baseStyles.title, 'fluid-font-t4-bold')}>
				{title}
			</Heading>

			{subtitle ? <p className={baseStyles.subtitle}>{subtitle}</p> : null}
		</Link>
	);
};
